import { useEffect, useState, useCallback } from 'react';
import { getUsageDashboard, UsageDashboard, QuotaInfo } from '../api/subscriptions';

interface UsageDashboardState extends Partial<UsageDashboard> {
  loading: boolean;
  error: string | null;
}

/**
 * Hook pour afficher le dashboard d'usage
 *
 * Charge les quotas, métriques, alertes et informations de trial
 * Refresh automatique toutes les 30 secondes
 *
 * @param autoRefreshMs - Intervalle de refresh automatique en ms (30000 par défaut)
 */
export function useUsageDashboard(autoRefreshMs: number = 30000) {
  const [state, setState] = useState<UsageDashboardState>({
    subscription: undefined,
    quotas: undefined,
    trial: undefined,
    alerts: [],
    upgrade_suggestion: undefined,
    loading: true,
    error: null
  });

  const loadDashboard = useCallback(async (isInitialLoad: boolean = false) => {
    // Ne montrer le loader que lors du chargement initial
    if (isInitialLoad) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const dashboard = await getUsageDashboard();
      setState({
        subscription: dashboard.subscription,
        quotas: dashboard.quotas,
        trial: dashboard.trial,
        alerts: dashboard.alerts,
        upgrade_suggestion: dashboard.upgrade_suggestion,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Failed to load usage dashboard:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to load usage dashboard'
      }));
    }
  }, []);

  useEffect(() => {
    // Chargement initial avec loader
    loadDashboard(true);

    // Auto-refresh si configuré (sans loader pour éviter le tremblement)
    if (autoRefreshMs > 0) {
      const intervalId = setInterval(() => {
        loadDashboard(false);
      }, autoRefreshMs);

      return () => clearInterval(intervalId);
    }
  }, [loadDashboard, autoRefreshMs]);

  /**
   * Rafraîchir manuellement le dashboard (sans loader)
   */
  const refresh = useCallback(() => {
    loadDashboard(false);
  }, [loadDashboard]);

  return {
    subscription: state.subscription,
    quotas: state.quotas,
    trial: state.trial,
    alerts: state.alerts,
    upgradeSuggestion: state.upgrade_suggestion,
    loading: state.loading,
    error: state.error,
    refresh
  };
}

/**
 * Calcule le pourcentage d'utilisation d'un quota
 */
export function getQuotaPercentage(quota: QuotaInfo): number {
  if (quota.unlimited) {
    return 0;
  }
  if (quota.limit === 0) {
    return 100;
  }
  return Math.min(100, Math.round((quota.used / quota.limit) * 100));
}

/**
 * Détermine la couleur du quota basée sur le pourcentage d'utilisation
 */
export function getQuotaColor(percentage: number): string {
  if (percentage >= 90) {
    return 'red';
  }
  if (percentage >= 80) {
    return 'orange';
  }
  if (percentage >= 60) {
    return 'yellow';
  }
  return 'green';
}

/**
 * Détermine si un quota est en alerte
 */
export function isQuotaInWarning(quota: QuotaInfo): boolean {
  if (quota.unlimited) {
    return false;
  }
  const percentage = getQuotaPercentage(quota);
  return percentage >= 80;
}

/**
 * Formate le quota pour affichage (ex: "5 / 10" ou "Illimité")
 */
export function formatQuotaDisplay(quota: QuotaInfo): string {
  if (quota.unlimited) {
    return 'Illimité';
  }
  return `${quota.used} / ${quota.limit}`;
}

/**
 * Formate le temps restant du trial
 */
export function formatTrialDaysRemaining(days: number): string {
  if (days <= 0) {
    return 'Expiré';
  }
  if (days === 1) {
    return '1 jour restant';
  }
  return `${days} jours restants`;
}

/**
 * Détermine la couleur de l'alerte trial
 */
export function getTrialAlertColor(daysRemaining: number): string {
  if (daysRemaining <= 0) {
    return 'red';
  }
  if (daysRemaining <= 3) {
    return 'red';
  }
  if (daysRemaining <= 7) {
    return 'orange';
  }
  return 'blue';
}

/**
 * Obtient l'icône appropriée pour un type de ressource
 */
export function getResourceIcon(resource: string): string {
  const icons: Record<string, string> = {
    architectures: '🏗️',
    projects: '📁',
    deployments: '🚀',
    pipeline_minutes: '⏱️',
    trial: '🎁'
  };
  return icons[resource] || '📊';
}

/**
 * Détermine si un message d'alerte devrait être affiché
 */
export function shouldShowAlert(severity: 'low' | 'medium' | 'high'): boolean {
  // Afficher toutes les alertes medium et high, ignorer low sauf configuration
  return severity === 'medium' || severity === 'high';
}
